import { BOOKING_TYPE_BY_REFERENCE_STORAGE_KEY } from '@/shared/constants/storage-keys';

/**
 * How the booking was made: b2c - the client booked themselves; b2b2c - the client was referred
 * via a B2B upsale link; b2b - a B2B partner booked for the client via a B2B link.
 */
export const BOOKING_TYPES = ['b2c', 'b2b', 'b2b2c'] as const;
export type BookingType = (typeof BOOKING_TYPES)[number];

const isBookingType = (value: string): value is BookingType =>
  (BOOKING_TYPES as readonly string[]).includes(value);

const normalizeReference = (reference: string) => reference.trim().toUpperCase();

/**
 * Decodes the booking link's `bkt` parameter (base64 of the booking type). Accepts URL-safe
 * base64 and missing padding. Anything that does not decode to a known type yields null.
 */
export const decodeBookingTypeParam = (raw: string | null | undefined): BookingType | null => {
  const encoded = raw?.trim().replace(/-/g, '+').replace(/_/g, '/');
  if (!encoded) return null;

  try {
    const padded = encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=');
    const decoded = atob(padded).trim().toLowerCase();
    return isBookingType(decoded) ? decoded : null;
  } catch {
    return null;
  }
};

const readMap = (): Record<string, BookingType> => {
  try {
    const raw = window.localStorage.getItem(BOOKING_TYPE_BY_REFERENCE_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};

    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, BookingType] =>
          typeof entry[1] === 'string' && isBookingType(entry[1])
      )
    );
  } catch {
    return {};
  }
};

/** Remembers the type a link carried for its booking reference; a newer link overrides it. */
export const rememberBookingType = (reference: string, bookingType: BookingType): void => {
  const key = normalizeReference(reference);
  if (!key) return;

  try {
    window.localStorage.setItem(
      BOOKING_TYPE_BY_REFERENCE_STORAGE_KEY,
      JSON.stringify({ ...readMap(), [key]: bookingType })
    );
  } catch {
    // Storage unavailable (private mode, blocked): the order falls back to what the API has.
  }
};

/** The type remembered for this reference, or undefined when no link set one. */
export const getRememberedBookingType = (
  reference: string | null | undefined
): BookingType | undefined => {
  const key = reference ? normalizeReference(reference) : '';
  return key ? readMap()[key] : undefined;
};
